import styled, { css } from 'styled-components'

import Icon from '../../../components/Icon'
import FormFieldCheckbox from '../FormFieldCheckbox'
import FormLabel from '../../FormLabel'
import dynamic from 'next/dynamic'

type InputStyleProps = {
  $isError?: boolean
  $isActive?: boolean
  $hasLabel?: boolean
  $isDisabled?: boolean
  $hasErrorMessage?: boolean
  $isSearchable?: boolean
}

const ReactSelect = dynamic(() => import('react-select'), { ssr: false })

export const Container = styled.div<InputStyleProps>`
  display: flex;
  flex-flow: column;

  // disabled
  ${({ $isDisabled }) =>
    $isDisabled === true &&
    css`
      opacity: 0.8;
      &,
      & * {
        cursor: not-allowed;
      }
    `}
`

export const InputContainer = styled.label<InputStyleProps>`
  width: 100%;
  display: flex;
  flex-direction: row;
  border: 1px solid ${({ theme }) => theme.colors.greyContour};
  background-color: ${({ theme }) => theme.colors.pureWhite};
  border-radius: 0.6rem;
  position: relative;

  ${({ $isError, theme }) =>
    $isError === true &&
    css`
      border-color: ${theme.colors.error};
    `}
`

export const DropDown = styled(Icon)`
  width: 1.5rem;
  position: absolute;
  color: ${({ theme }) => theme.colors.greyContour};
  top: calc(100%);
  right: 1rem;
`

export const Checkbox = styled(FormFieldCheckbox)`
  padding-left: 2rem;
`

export const selectStyles = css<InputStyleProps>`
  appearance: none;
  // Additional resets for further consistency
  background-color: transparent;
  border: none;
  margin: 0;
  width: 100%;
  font-family: inherit;
  font-size: inherit;
  cursor: inherit;
  outline: none;
  position: relative;

  ${({ $isDisabled }) =>
    $isDisabled === true &&
    css`
      opacity: 0.5;
    `}

  ${({ theme }) => theme.textStyles.montserratTextBase};
  line-height: 1;
  border: 0;
  color: ${({ theme }) => theme.colors.blueSecondaryBis};

  // disabled
  ${({ $isDisabled }) =>
    $isDisabled === true &&
    css`
      pointer-events: none;
    `}
  &::placeholder {
    opacity: 0.5;
  }

  // readonly
  &[readonly] {
    cursor: default;
  }

  &:focus {
    outline: none;
  }

  &[type='password'] {
    letter-spacing: 3px;
  }

  //  React Select

  .react-select__control {
    min-height: 4rem;

    ${({ $isActive }) =>
      $isActive === true &&
      css`
        border-color: ${({ theme }) => theme.colors.bluePrimary};
        background-color: ${({ theme }) => theme.colors.blueLight};
      `}

    ${({ $isError, theme }) =>
      $isError === true &&
      css`
        border-color: ${theme.colors.error};
      `}
  }

  .react-select__placeholder {
    ${({ theme }) => theme.textStyles.montserratSm};
    color: ${({ theme }) => theme.colors.dustyGray};
    padding-left: 2rem;

    ${({ $isActive }) =>
      $isActive === true &&
      css`
        color: ${({ theme }) => theme.colors.bluePrimary};
      `}
  }

  ${({ $isSearchable }) =>
    $isSearchable === true &&
    css`
      .react-select__control:not(.react-select__control--menu-is-open) {
        .react-select__input-container {
          //opacity: 0;
          display: none;
        }
      }

      .react-select__control--menu-is-open {
        svg {
          display: none;
        }
        .react-select__placeholder {
          //opacity: 0;
          display: none;
        }
      }
    `}

  ${({ $isSearchable }) =>
    $isSearchable === false &&
    css`
      .react-select__control--menu-is-open {
        background: ${({ theme }) => theme.colors.bluePrimary};
        transition: background 0.3s ease;

        .react-select__placeholder {
          color: ${({ theme }) => theme.colors.pureWhite};
        }

        .react-select__dropdown-indicator svg {
          fill: ${({ theme }) => theme.colors.pureWhite};
        }
      }
    `}
  .react-select__input-container {
    ${({ theme }) => theme.textStyles.montserratSm};
    border: 1px solid ${({ theme }) => theme.colors.greyContour};
    border-radius: 1rem;
    padding: 0.2rem 1rem;
    color: ${({ theme }) => theme.colors.greyContour};
  }

  .react-select__indicator-separator,
  .react-select__clear-indicator {
    display: none;
  }

  .react-select__option--is-selected,
  .react-select__option--is-focused {
    background-color: ${({ theme }) => theme.colors.blueLight};
  }

  .react-select__menu-portal {
    z-index: 3;
  }

  .react-select__value-container {
    .react-select__placeholder {
      visibility: hidden;
      position: absolute;
    }
  }

  .react-select__control:has(div):has(
      .react-select__value-container--has-value
    ) {
    .react-select__value-container {
      flex: initial;
    }

    .react-select__placeholder {
      order: 2;
    }

    .react-select__indicators {
      order: 3;
    }

    .react-select__value-container--is-multi {
      order: 4;
      overflow: auto;
      max-height: 10rem;
      overscroll-behavior: contain;
      width: 100%;

      /* Hide scrollbar for IE, Edge and Firefox */
      -ms-overflow-style: none; /* IE and Edge */
      scrollbar-width: none; /* Firefox */

      /* Hide scrollbar for Chrome, Safari and Opera */
      &::-webkit-scrollbar {
        display: none;
      }
    }

    .react-select__multi-value {
      background-color: ${({ theme }) => theme.colors.bluePrimary};
      border-radius: 100px;
      padding: 8px 1.6rem;

      .react-select__multi-value__label {
        color: ${({ theme }) => theme.colors.pureWhite};
      }

      .react-select__multi-value__remove {
        border-radius: 16px;
        background-color: rgba(217, 217, 217, 0.3);
        cursor: pointer;
        margin-left: 1rem;
      }

      svg {
        fill: ${({ theme }) => theme.colors.pureWhite};
      }
    }
  }
`
export const Input = styled(ReactSelect)<InputStyleProps>`
  ${selectStyles}

  &:focus {
    outline: 0.1rem dotted #000;
  }
  &:focus:not(:focus-visible) {
    outline: none;
  }
  &:focus-visible {
    outline: 0.1rem dotted #000;
  }
`

const HintMessage = styled.p`
  ${({ theme }) => theme.textStyles.montserratTextBase}
  margin-top: 0.5rem;
  padding-inline: 0.8rem;
  text-align: left;
`
export const HelpMessage = styled(HintMessage)`
  color: ${({ theme }) => theme.colors.bismark};
`
export const ErrorMessage = styled(HintMessage)`
  color: ${({ theme }) => theme.colors.error};
`

export const Label = styled(FormLabel)<{
  $required?: boolean
  $active?: boolean
}>`
  transition: all 0.3s ease;

  ${({ $active }) =>
    $active === true &&
    css`
      color: ${({ theme }) => theme.colors.bluePrimary};
    `}
`

export const LeftSlotPlaceholderContainer = styled.div`
  display: flex;
  &:has(> span) {
    .react-select__placeholder {
      padding-left: 0;
    }
  }
`

export const LeftSlot = styled.span`
  margin-left: 1rem;
  margin-right: 1rem;
`
